/**
 * A thrown grenade or smoke canister in flight (02 §7, ROUND7 §4). It flies an arc from the hand to the aim
 * point, and anything solid taller than the arc where it crosses - a wall, a building - stops it: it drops on
 * the near side. It used to fly a straight lerp through everything, so a grenade lobbed at a compound wall
 * went off on the far side of it. The ranger's and the garrison's are the same object and obey the same rule.
 *
 * Pure: the arc is a parabola in the throw's own parameter, the crossing test is `segmentHit` on the statics.
 */

import type { Ctx } from './ctx';
import { clamp, length } from './math';
import type { Collider } from './props';
import { segmentHit, type Statics } from './statics';
import { WALLS } from './tuningWalls';
import type { Thrown } from './types';

/** The arc's highest point for a throw of this length, metres above the ground (never below the hand). */
export function grenadeApex(throwLength: number): number {
  const { fraction, min, max } = WALLS.grenadeApex;
  return Math.max(WALLS.grenadeHand, clamp(throwLength * fraction, min, max));
}

/**
 * Height of the arc at `u` (0 at the hand, 1 on the ground at the aim point) for a throw whose highest point is
 * `apex`: the parabola through (0, hand) and (1, 0) that peaks at `apex`.
 */
export function arcHeight(u: number, apex: number): number {
  const hand = WALLS.grenadeHand;
  const rise = Math.max(0, apex - hand);
  const b = 2 * rise + 2 * Math.sqrt(rise * Math.max(apex, hand));
  return hand + b * u - (hand + b) * u * u;
}

/** True if (x, z) lies inside the collider's own footprint: a man throwing from inside a building. */
function inside(c: Collider, x: number, z: number): boolean {
  if (c.shape === 'circle') return (x - c.x) * (x - c.x) + (z - c.z) * (z - c.z) < c.r * c.r;
  return Math.abs(x - c.x) < c.hx && Math.abs(z - c.z) < c.hz;
}

/**
 * Where along the throw (0-1) it first strikes something too tall to clear, if it does so in [from, to); or null.
 * Wire does not stop it (it is not solid), nor does whatever the thrower stands inside.
 */
export function firstStop(s: Statics, g: Thrown, from: number, to: number): number | null {
  const apex = grenadeApex(length(g.endX - g.startX, g.endZ - g.startZ));
  let best: number | null = null;
  for (const c of s.colliders) {
    if (!c.blocksSight || inside(c, g.startX, g.startZ)) continue;
    const hit = segmentHit(c, g.startX, g.startZ, g.endX, g.endZ, WALLS.body);
    if (!hit || hit.enter < from || hit.enter >= to || (best !== null && hit.enter >= best)) continue;
    // The arc is highest between the two edges, so it clears the whole footprint if it clears both of them.
    if (Math.min(arcHeight(hit.enter, apex), arcHeight(hit.exit, apex)) < c.h) best = hit.enter;
  }
  return best;
}

/** It struck something at `u`: it falls at the foot of it, on the thrower's side, and lies there from now on. */
function drop(g: Thrown, u: number): void {
  const dx = g.endX - g.startX;
  const dz = g.endZ - g.startZ;
  const span = length(dx, dz);
  const back = span > 0 ? Math.max(0, u - WALLS.dropBack / span) : 0;
  g.x = g.startX + dx * back;
  g.z = g.startZ + dz * back;
  // Where it lies is where it will go off: the garrison's attribution and the HUD's noise ring read the end point.
  g.endX = g.x;
  g.endZ = g.z;
  g.y = 0.1;
  g.flightTicks = g.flightTotal;
}

/** One tick of a grenade still in the air. */
export function flyThrown(ctx: Ctx, g: Thrown): void {
  const from = g.flightTicks / g.flightTotal;
  g.flightTicks++;
  const t = g.flightTicks / g.flightTotal;
  const stop = firstStop(ctx.statics, g, from, t);
  if (stop !== null) {
    drop(g, stop);
    return;
  }
  g.x = g.startX + (g.endX - g.startX) * t;
  g.z = g.startZ + (g.endZ - g.startZ) * t;
  g.y = arcHeight(t, grenadeApex(length(g.endX - g.startX, g.endZ - g.startZ)));
}
